import { useState } from "react";
import { Info, CaretDown, CaretRight, ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact legend explaining what LOW / MEDIUM / HIGH confidence bands mean
 * and — when ``bots`` are supplied — showing where each active bot currently
 * sits on the 0-100 scale.
 *
 * Hovering a marker reveals a tooltip with the exact score, band, and how
 * long ago the score was recomputed — so users know how fresh the number is
 * before they trust it.
 *
 * Score ranges match backend/bot_confidence.py:
 *   HIGH   ≥ 75
 *   MEDIUM 50 – 74
 *   LOW    < 50
 */
const BANDS = [
  { key: "low",    label: "LOW",    range: "< 50",   color: "#FF3B30", desc: "Weak backtest — bot trades at 0.5× size when scaling is on" },
  { key: "medium", label: "MEDIUM", range: "50 – 74", color: "#F59E0B", desc: "Baseline — bot trades at 1.0× size" },
  { key: "high",   label: "HIGH",   range: "≥ 75",   color: "#00C805", desc: "Strong edge — bot trades at 1.5× size" },
];

function bandColor(band) {
  return BANDS.find((b) => b.key === band)?.color || "#a1a1aa";
}

function formatAgo(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "just now";
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Compact 100-wide SVG sparkline of a bot's confidence-score history. Line
 * colour reflects the *direction* of the trend (delta = last - first) so
 * users can spot rising vs falling bots at a glance without reading axis
 * labels. Includes a faint 50-threshold guide line so borderline pauses
 * are visually anchored.
 */
function TrendSpark({ history, testId }) {
  const W = 100;
  const H = 24;
  const pad = 2;
  if (!Array.isArray(history) || history.length < 2) return null;
  const delta = history[history.length - 1] - history[0];
  const stroke = delta > 0 ? "#00C805" : delta < 0 ? "#FF3B30" : "#a1a1aa";
  const stepX = (W - pad * 2) / (history.length - 1);
  const y = (v) => H - pad - (Math.max(0, Math.min(100, v)) / 100) * (H - pad * 2);
  const points = history.map((v, i) => `${(pad + i * stepX).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const thresholdY = y(50);
  return (
    <svg data-testid={testId} width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="mt-1">
      {/* 50-threshold guide */}
      <line x1={0} x2={W} y1={thresholdY} y2={thresholdY}
            stroke="rgba(255,255,255,0.15)" strokeDasharray="2 2" strokeWidth="0.75" />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ConfidenceBandsLegend({ bots = [], onRefresh }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(null); // symbol currently recomputing
  const [refreshingAll, setRefreshingAll] = useState(false);

  // Pull cached confidence off the bot doc when available so the legend
  // renders instant markers without a per-bot round-trip. Payload shape:
  // { confidence_cache: { payload: {score, band, ...}, computed_at } }
  const scored = bots
    .map((b) => {
      const p = b?.confidence_cache?.payload || b?.confidence_cache || null;
      const s = p?.score;
      if (typeof s !== "number") return null;
      // Trim history to the last 30 entries — enough for a legible sparkline
      // without shipping the full 60-entry buffer in every list_bots poll.
      const rawHistory = Array.isArray(b?.confidence_history) ? b.confidence_history : [];
      const history = rawHistory.slice(-30).map((h) => Number(h?.score)).filter(Number.isFinite);
      return {
        id: b.id,
        symbol: b.symbol,
        score: s,
        band: p?.band || null,
        computed_at: b?.confidence_cache?.computed_at || null,
        history,
      };
    })
    .filter(Boolean);

  const recompute = async (marker) => {
    if (!marker.id) return;
    setRefreshing(marker.symbol);
    try {
      await api.get(`/bots/${marker.id}/confidence?force=true`);
      toast.success(`${marker.symbol} score refreshed`);
      onRefresh?.();
    } catch {
      toast.error(`Couldn't refresh ${marker.symbol} — try again in a moment`);
    } finally {
      setRefreshing(null);
    }
  };

  // Bulk recompute — fires per-bot force refreshes sequentially so we don't
  // hammer the market_data cache or Claude with 10 parallel requests. Toast
  // gives users a single summary at the end.
  const recomputeAll = async () => {
    if (scored.length === 0) return;
    setRefreshingAll(true);
    let ok = 0;
    let failed = 0;
    for (const marker of scored) {
      if (!marker.id) continue;
      setRefreshing(marker.symbol);
      try {
        await api.get(`/bots/${marker.id}/confidence?force=true`);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setRefreshing(null);
    setRefreshingAll(false);
    if (failed === 0) toast.success(`Refreshed ${ok} bot score${ok === 1 ? "" : "s"}`);
    else toast.info(`Refreshed ${ok} — ${failed} failed, try again shortly`);
    onRefresh?.();
  };

  return (
    <div
      data-testid="confidence-bands-legend"
      className="border border-zinc-800 rounded-sm bg-zinc-900/40"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="confidence-bands-toggle"
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-zinc-900/70 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Info size={13} className="text-zinc-500" />
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-400">
            Confidence bands · how they work
          </span>
        </div>
        {open
          ? <CaretDown size={12} className="text-zinc-500" />
          : <CaretRight size={12} className="text-zinc-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3" data-testid="confidence-bands-body">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {BANDS.map((b) => (
              <div
                key={b.key}
                data-testid={`confidence-band-${b.key}`}
                className="border border-zinc-800 rounded-sm px-3 py-2.5"
                style={{ borderLeft: `3px solid ${b.color}` }}
              >
                <div className="flex items-baseline justify-between">
                  <span className="mono text-xs font-bold" style={{ color: b.color }}>{b.label}</span>
                  <span className="mono text-[10px] text-zinc-500">{b.range}</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-1 leading-snug">{b.desc}</div>
              </div>
            ))}
          </div>
          {/* Where each bot sits on the scale */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                Your bots on the scale
              </div>
              {scored.length > 0 && (
                <button
                  type="button"
                  data-testid="confidence-refresh-all-btn"
                  disabled={refreshingAll}
                  onClick={recomputeAll}
                  title={`Force-recompute confidence for all ${scored.length} bots`}
                  className="mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-white/20 text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-wait transition-colors inline-flex items-center gap-1"
                >
                  {refreshingAll
                    ? <><CircleNotch size={10} className="animate-spin" /> Recomputing {scored.length}…</>
                    : <><ArrowsClockwise size={10} weight="bold" /> Recompute all</>
                  }
                </button>
              )}
            </div>
            <div
              className="relative h-6 rounded-sm overflow-hidden"
              data-testid="confidence-bands-scale"
              style={{
                background:
                  "linear-gradient(to right, rgba(255,59,48,0.35) 0%, rgba(255,59,48,0.35) 50%, rgba(245,158,11,0.35) 50%, rgba(245,158,11,0.35) 75%, rgba(0,200,5,0.35) 75%, rgba(0,200,5,0.35) 100%)",
              }}
            >
              {/* Band dividers */}
              <div className="absolute inset-y-0" style={{ left: "50%", width: 1, background: "rgba(255,255,255,0.15)" }} />
              <div className="absolute inset-y-0" style={{ left: "75%", width: 1, background: "rgba(255,255,255,0.15)" }} />
              {scored.map((s) => {
                const pct = Math.max(0, Math.min(100, s.score));
                const c = bandColor(s.band);
                const isRefreshing = refreshing === s.symbol;
                // Trend delta from confidence_history — used to tint the
                // marker's outer ring so falling bots stand out on the
                // scale without users opening every popover.
                const trendDelta = s.history.length >= 2
                  ? s.history[s.history.length - 1] - s.history[0] : 0;
                const ringColor = trendDelta > 0
                  ? "#00C805" : trendDelta < 0 ? "#FF3B30" : "rgba(0,0,0,0.4)";
                const trendGlyph = trendDelta > 0 ? "▲" : trendDelta < 0 ? "▼" : "";
                return (
                  <Popover key={s.symbol}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={`confidence-band-marker-${s.symbol}`}
                        data-trend={trendDelta > 0 ? "up" : trendDelta < 0 ? "down" : "flat"}
                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center cursor-pointer group"
                        style={{ left: `${pct}%` }}
                      >
                        <div
                          className="w-1.5 h-4 rounded-full ring-2 group-hover:scale-125 transition-transform"
                          style={{ background: c, boxShadow: `0 0 0 2px ${ringColor}` }}
                        />
                        <div className="mono text-[8px] text-white/80 -mt-0.5 leading-none flex items-center gap-0.5">
                          <span>{s.symbol}</span>
                          {trendGlyph && (
                            <span
                              data-testid={`confidence-marker-trend-${s.symbol}`}
                              style={{ color: ringColor }}
                              className="text-[7px] font-bold leading-none"
                            >{trendGlyph}</span>
                          )}
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      sideOffset={6}
                      className="bg-zinc-950 border border-zinc-800 rounded-sm p-0 shadow-xl w-56"
                      data-testid={`confidence-band-tooltip-${s.symbol}`}
                    >
                      <div className="px-3 py-2.5 border-b border-zinc-800/70">
                        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                          {s.symbol}
                        </div>
                        <div className="mono text-sm font-bold" style={{ color: c }}>
                          {s.score.toFixed(1)}
                          {s.band && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-widest opacity-90">
                              {s.band}
                            </span>
                          )}
                        </div>
                        <div
                          className="mono text-[10px] text-zinc-500 mt-1"
                          data-testid={`confidence-band-fresh-${s.symbol}`}
                        >
                          Recomputed {formatAgo(s.computed_at)}
                        </div>
                        {s.history.length >= 2 && (
                          <>
                            <TrendSpark
                              history={s.history}
                              testId={`confidence-trend-spark-${s.symbol}`}
                            />
                            <div className="mono text-[9px] text-zinc-500 mt-0.5 flex items-center justify-between">
                              <span>Trend · {s.history.length}pt</span>
                              <span style={{
                                color: s.history[s.history.length - 1] - s.history[0] > 0
                                  ? "#00C805"
                                  : s.history[s.history.length - 1] - s.history[0] < 0
                                  ? "#FF3B30" : "#a1a1aa"
                              }}>
                                {(() => {
                                  const d = s.history[s.history.length - 1] - s.history[0];
                                  return `${d > 0 ? "▲+" : d < 0 ? "▼" : "·"}${Math.abs(d).toFixed(1)}`;
                                })()}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => recompute(s)}
                        disabled={isRefreshing || !s.id}
                        data-testid={`confidence-refresh-${s.symbol}`}
                        className="w-full px-3 py-2 flex items-center justify-center gap-1.5 mono text-[10px] uppercase tracking-widest text-white hover:bg-zinc-900/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isRefreshing
                          ? <><CircleNotch size={11} className="animate-spin" /> Recomputing…</>
                          : <><ArrowsClockwise size={11} weight="bold" /> Refresh score</>
                        }
                      </button>
                    </PopoverContent>
                  </Popover>
                );
              })}
            </div>
            {scored.length === 0 && (
              <div className="mono text-[10px] text-zinc-500 mt-1">
                No cached scores yet — deploy a bot with confidence sizing to populate this scale.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
